import { every, filter, find, first, get, map, some } from 'lodash/fp';
import { flow, pipe } from 'fp-ts/function';
import { createSelector } from 'reselect';
import { array, record } from 'fp-ts';
import { AccountingState, OrderItemType, OrderItem as OrderItemGenerated } from '@mews-commander/data-access';
import { OrderItem } from '@mews-commander/types';
import { getProp, getStringArrayProp, getStringProp } from '@mews/fp';
import { composeProps, createPropsExtender, ifProps } from '@mews/framework';
import { Optional } from '@mews/std';
import { isStringArraySubsetOfStringArray } from '@mews-commander/utils';
import {
    getOrderItem,
    getOrderItemIdsByBillId,
    getOrderItems,
    getOrderItemsByBillId,
} from '@mews-commander/legacy-entities/src/ducks/orderItems';
import { getOrderItemsByGroupId, getBillOrderItemsByServiceOrderId } from '../../selectors/grouping';

export const isOrderItemRebate = (item: OrderItem) =>
    item.data.discriminator === OrderItemType.AdditionalExpenseRebate ||
    item.data.discriminator === OrderItemType.ProductOrderRebate ||
    item.data.discriminator === OrderItemType.NightRebate;

export const isOrderItemClosed = (item: OrderItem) => item.accountingState === AccountingState.Closed;

export const isOrderItemTaxCorrection = (item: OrderItem) => item.data.discriminator === OrderItemType.TaxCorrection;

export const isOrderItemRebated = (item: OrderItem) => item.isRebated;

export const getOrderItemRebateReason = (item: OrderItem): Optional<string> => get('data.value.rebateReason', item);

export const getOrderItemRebateIndex = (item: OrderItem): number | undefined => get('data.value.rebateIndex', item);

const getOrderItemRebateOriginalItemId = (item: OrderItem): string | undefined =>
    get('data.value.originalItemId', item);

const getOrderItemRebateOriginalItemIdSelector = createSelector(
    getProp<OrderItem>()('orderItem'),
    getOrderItemRebateOriginalItemId
);

const getRebateOrderItems = createSelector(getOrderItems, filter(isOrderItemRebate));

export const getRebateByRebatedItemId = createSelector(
    getStringProp('rebatedItemId'),
    getRebateOrderItems,
    (rebatedItemId, orderItems) => find((item) => getOrderItemRebateOriginalItemId(item) === rebatedItemId, orderItems)
);

export const getRebateOriginalItem = composeProps(
    ifProps(['id'], getOrderItem),
    createPropsExtender('id', getOrderItemRebateOriginalItemIdSelector)
);

const getRebatedOrderItems = createSelector(getOrderItems, filter(isOrderItemRebated));

const getRebatedOrderItemIds = createSelector(getRebatedOrderItems, map('id'));

const getClosedRebatedOrderItems = createSelector(getRebatedOrderItems, filter(isOrderItemClosed));

const getClosedRebatedOrderItemIds = createSelector(getClosedRebatedOrderItems, map('id'));

export const getTaxCorrectionOrderItemIds = createSelector(
    getOrderItems,
    flow(record.filter(isOrderItemTaxCorrection), record.keys)
);

// @ts-expect-error
export const isRebatedOrderItem = createSelector(getOrderItem, isOrderItemRebated);

export const getOrderItemCancellationReason = (orderItem: OrderItemGenerated) => {
    if (!orderItem) return null;
    if (orderItem.data.discriminator !== OrderItemType.CancellationFee) return null;

    return orderItem.data.value?.cancellationFeeReason;
};

export const areAllBillOrderItemsRebated = createSelector(
    getOrderItemIdsByBillId,
    getRebatedOrderItemIds,
    isStringArraySubsetOfStringArray
);

export const areAllServiceOrderItemsRebated = createSelector(
    getBillOrderItemsByServiceOrderId,
    every(isOrderItemRebated)
);

export const areSomeServiceOrderItemsRebated = createSelector(
    getBillOrderItemsByServiceOrderId,
    some(isOrderItemRebated)
);

export const areAllGroupOrderItemsRebated = createSelector(getOrderItemsByGroupId, every(isOrderItemRebated));

export const areSomeGroupOrderItemsRebated = createSelector(getOrderItemsByGroupId, some(isOrderItemRebated));

export const getAreAllBillOrderItemsRebated = createSelector(getOrderItemsByBillId, every(isOrderItemRebated));

export const getAreSomeBillOrderItemsRebated = createSelector(getOrderItemsByBillId, some(isOrderItemRebated));

export const getSelectableOrderItemIds = createSelector(
    getStringArrayProp('orderItemIds'),
    getClosedRebatedOrderItemIds,
    getTaxCorrectionOrderItemIds,
    (orderItemIds, rebatedOrderItemIds, taxCorrectionOrderItemIds) =>
        pipe(
            orderItemIds,
            array.filter((id) => !rebatedOrderItemIds.includes(id) && !taxCorrectionOrderItemIds.includes(id))
        )
);

export const areAllOrderItemsByGroupIdRebates = createSelector(getOrderItemsByGroupId, every(isOrderItemRebate));

export const areAllOrderItemsByGroupIdClosed = createSelector(getOrderItemsByGroupId, every(isOrderItemClosed));

export const areAllServiceOrderItemsRebates = createSelector(
    getBillOrderItemsByServiceOrderId,
    every(isOrderItemRebate)
);

export const areAllServiceOrderItemsClosed = createSelector(
    getBillOrderItemsByServiceOrderId,
    every(isOrderItemClosed)
);

const getCollectiveRebateIndex = (orderItems: OrderItem[]) => {
    const firstOrderItem = first(orderItems);

    if (firstOrderItem) {
        const doAllHaveSameIndex = every(
            (item) => getOrderItemRebateIndex(item) === getOrderItemRebateIndex(firstOrderItem),
            orderItems
        );
        if (doAllHaveSameIndex) return getOrderItemRebateIndex(firstOrderItem);
    }
};

export const getGroupItemsCollectiveRebateIndex = createSelector(getOrderItemsByGroupId, getCollectiveRebateIndex);

export const getServiceOrderItemsCollectiveRebateIndex = createSelector(
    getBillOrderItemsByServiceOrderId,
    getCollectiveRebateIndex
);
